import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { type Capabilities, useCapabilities, usePermissions } from "@/features/account/useCurrentUser";
import {
  HELP_ARTICLES,
  HELP_GROUP_GATE,
  HELP_GROUPS,
  type HelpGate,
  type HelpGroupId,
  type HelpSlug,
} from "./articles";
import { HELP_CONTENT } from "./content";
import { type ParsedArticle, parseArticle } from "./parseArticle";

export interface HelpArticle extends ParsedArticle {
  slug: HelpSlug;
  group: HelpGroupId;
  startHere: boolean;
  related: readonly HelpSlug[];
}

export interface HelpReader {
  isPlatformAdmin: boolean;
  capabilities: Capabilities;
}

export function gateAllows(gate: HelpGate | undefined, reader: HelpReader): boolean {
  if (!gate || gate === "all") return true;
  if (gate === "platformAdmin") return reader.isPlatformAdmin;
  const capability = gate.slice("capability:".length) as keyof Capabilities;
  return reader.capabilities[capability] === true;
}

/** Every article this reader may see, in nav order, with gated sections resolved. Pure: tested directly. */
export function visibleArticles(reader: HelpReader, language: string): HelpArticle[] {
  const sources = HELP_CONTENT[language] ?? HELP_CONTENT.en;
  const allowed = (gate: HelpGate) => gateAllows(gate, reader);
  const visible = HELP_ARTICLES.filter(
    (meta) => allowed(HELP_GROUP_GATE[meta.group] ?? "all") && allowed("gate" in meta ? meta.gate : "all"),
  );
  const slugs = new Set<string>(visible.map((meta) => meta.slug));
  return HELP_GROUPS.flatMap((group) =>
    visible
      .filter((meta) => meta.group === group)
      .map((meta) => ({
        ...parseArticle(sources?.[meta.slug] ?? HELP_CONTENT.en?.[meta.slug] ?? "", allowed),
        slug: meta.slug,
        group: meta.group,
        startHere: "startHere" in meta && meta.startHere === true,
        // Related links never point at an article the reader can't open.
        related: (meta.related as readonly HelpSlug[]).filter((slug) => slugs.has(slug)),
      })),
  );
}

/** The current reader's articles (role + deployment capabilities + language). */
export function useHelpArticles(): HelpArticle[] {
  const { isPlatformAdmin } = usePermissions();
  const capabilities = useCapabilities();
  const { i18n } = useTranslation();
  const capsKey = JSON.stringify(capabilities);
  // biome-ignore lint/correctness/useExhaustiveDependencies: capsKey stands in for the (fresh each render) capabilities object
  return useMemo(
    () => visibleArticles({ isPlatformAdmin, capabilities }, i18n.language),
    [isPlatformAdmin, capsKey, i18n.language],
  );
}
